import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Check, X, Loader2, ExternalLink, ShieldCheck, ArrowLeft, MessageSquare, BadgeCheck, Share2 } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import api from '../services/api';

const BRAND_BLUE = '#2B4B6A';

const StatusPill = ({ status }) => (
  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
    status === 'approved' ? 'bg-green-50 text-green-700' :
    status === 'rejected' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'
  }`}>{status}</span>
);

const AdminDirectoryClaims = () => {
  const [tab, setTab] = useState('claims');
  const [items, setItems] = useState([]);
  const [comments, setComments] = useState([]);
  const [shares, setShares] = useState({ items: [], by_channel: {}, top_listings: [], total: 0 });
  const [channelFilter, setChannelFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [busyId, setBusyId] = useState(null);

  const fetchClaims = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/directory/claims', { params: { status: statusFilter } });
      setItems(res.data.items);
    } catch (e) {
      toast.error('Failed to load claims');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchComments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/directory/comments', { params: { status: statusFilter } });
      setComments(res.data.items);
    } catch (e) {
      toast.error('Failed to load comments');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchShares = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/directory/shares', { params: { channel: channelFilter } });
      setShares(res.data);
    } catch (e) {
      toast.error('Failed to load shares');
    } finally {
      setLoading(false);
    }
  }, [channelFilter]);

  useEffect(() => {
    if (tab === 'claims') fetchClaims();
    else if (tab === 'comments') fetchComments();
    else fetchShares();
  }, [tab, fetchClaims, fetchComments, fetchShares]);

  const act = async (claim, action) => {
    setBusyId(claim.id);
    try {
      await api.post(`/admin/directory/claims/${claim.id}/${action}`);
      toast.success(action === 'approve' ? 'Claim approved' : 'Claim rejected');
      fetchClaims();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const actComment = async (comment, action) => {
    setBusyId(comment.id);
    try {
      await api.post(`/admin/directory/comments/${comment.id}/${action}`);
      toast.success(action === 'approve' ? 'Comment approved' : 'Comment rejected');
      fetchComments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Sidebar>
      <div className="p-6 md:p-8 bg-gray-50/50 min-h-screen">
        <Link to="/admin/directory" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-3">
          <ArrowLeft className="w-4 h-4" /> Back to directory
        </Link>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Directory Moderation</h1>
            <p className="text-sm text-gray-500 mt-1">Review listing claims, user comments and directory shares</p>
          </div>
          {tab === 'shares' ? (
            <select value={channelFilter} onChange={(e) => setChannelFilter(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white" data-testid="moderation-channel-filter">
              <option value="all">All channels</option>
              <option value="email">Sent to a friend</option>
              <option value="copy">Link copied</option>
              <option value="linkedin">LinkedIn</option>
              <option value="x">X</option>
              <option value="whatsapp">WhatsApp</option>
            </select>
          ) : (
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white" data-testid="moderation-status-filter">
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="all">All</option>
            </select>
          )}
        </div>

        <div className="flex items-center gap-2 mb-5">
          <button
            onClick={() => setTab('claims')}
            data-testid="moderation-tab-claims"
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border ${
              tab === 'claims' ? 'text-white border-transparent' : 'text-gray-600 border-gray-200 bg-white hover:bg-gray-50'
            }`}
            style={tab === 'claims' ? { backgroundColor: BRAND_BLUE } : {}}
          >
            <BadgeCheck className="w-4 h-4" /> Claims
          </button>
          <button
            onClick={() => setTab('comments')}
            data-testid="moderation-tab-comments"
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border ${
              tab === 'comments' ? 'text-white border-transparent' : 'text-gray-600 border-gray-200 bg-white hover:bg-gray-50'
            }`}
            style={tab === 'comments' ? { backgroundColor: BRAND_BLUE } : {}}
          >
            <MessageSquare className="w-4 h-4" /> Comments
          </button>
          <button
            onClick={() => setTab('shares')}
            data-testid="moderation-tab-shares"
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border ${
              tab === 'shares' ? 'text-white border-transparent' : 'text-gray-600 border-gray-200 bg-white hover:bg-gray-50'
            }`}
            style={tab === 'shares' ? { backgroundColor: BRAND_BLUE } : {}}
          >
            <Share2 className="w-4 h-4" /> Shares
          </button>
        </div>

        {tab === 'shares' && (
          <div className="grid gap-4 md:grid-cols-2 mb-5">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">By channel</h2>
              {Object.keys(shares.by_channel || {}).length === 0 ? (
                <p className="text-sm text-gray-400">Nothing shared yet.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {Object.entries(shares.by_channel).sort((a, b) => b[1] - a[1]).map(([ch, n]) => (
                    <span key={ch} className="px-3 py-1.5 rounded-full text-sm font-medium bg-blue-50" style={{ color: BRAND_BLUE }}>
                      {ch} · {n}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Most shared listings</h2>
              {(shares.top_listings || []).length === 0 ? (
                <p className="text-sm text-gray-400">Nothing shared yet.</p>
              ) : (
                <ul className="space-y-1.5">
                  {shares.top_listings.map((t) => (
                    <li key={t.slug} className="flex items-center justify-between text-sm">
                      <Link to={`/directory/${t.slug}`} target="_blank" className="text-gray-700 hover:underline truncate">{t.name}</Link>
                      <span className="text-gray-400 tabular-nums ml-3">{t.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            {tab === 'shares' ? (
              <table className="w-full">
                <thead className="bg-gray-50/80 border-b border-gray-100">
                  <tr>
                    <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase">Company</th>
                    <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase">Channel</th>
                    <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase">From</th>
                    <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase">To</th>
                    <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase">Note</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {loading ? (
                    <tr><td colSpan={5} className="px-5 py-10 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
                  ) : (shares.items || []).length === 0 ? (
                    <tr><td colSpan={5} className="px-5 py-10 text-center text-gray-400">No shares recorded yet</td></tr>
                  ) : shares.items.map((s) => (
                    <tr key={s.id} className="hover:bg-gray-50/50 transition-colors align-top" data-testid="share-row">
                      <td className="px-5 py-3">
                        <Link to={`/directory/${s.slug}`} target="_blank" className="font-medium text-gray-800 hover:underline flex items-center gap-1">
                          {s.company_name} <ExternalLink className="w-3 h-3 text-gray-400" />
                        </Link>
                        {s.created_at && <div className="text-xs text-gray-400 mt-0.5">{new Date(s.created_at).toLocaleString()}</div>}
                      </td>
                      <td className="px-5 py-3">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50" style={{ color: BRAND_BLUE }}>{s.channel}</span>
                        {s.channel === 'email' && s.delivered === false && (
                          <div className="text-[11px] text-red-600 mt-1">delivery failed</div>
                        )}
                      </td>
                      <td className="px-5 py-3 text-sm text-gray-600">
                        {s.from_name ? (<><div>{s.from_name}</div><div className="text-xs text-gray-400">{s.from_email}</div></>) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-5 py-3 text-sm text-gray-600">
                        {s.to_email ? (<><div>{s.to_name || '—'}</div><div className="text-xs text-gray-400">{s.to_email}</div></>) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-5 py-3 text-sm text-gray-700 max-w-sm"><p className="whitespace-pre-wrap break-words">{s.message || <span className="text-gray-300">—</span>}</p></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : tab === 'claims' ? (
              <table className="w-full">
                <thead className="bg-gray-50/80 border-b border-gray-100">
                  <tr>
                    <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase">Company</th>
                    <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase">Requested by</th>
                    <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase">Domain match</th>
                    <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="text-right px-5 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {loading ? (
                    <tr><td colSpan={5} className="px-5 py-10 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
                  ) : items.length === 0 ? (
                    <tr><td colSpan={5} className="px-5 py-10 text-center text-gray-400">No claims</td></tr>
                  ) : items.map((c) => (
                    <tr key={c.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-5 py-3">
                        <Link to={`/directory/${c.slug}`} target="_blank" className="font-medium text-gray-800 hover:underline flex items-center gap-1">
                          {c.directory_company_name} <ExternalLink className="w-3 h-3 text-gray-400" />
                        </Link>
                      </td>
                      <td className="px-5 py-3 text-sm text-gray-600">{c.user_email}</td>
                      <td className="px-5 py-3">
                        {c.matched_domain ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                            <ShieldCheck className="w-3 h-3" /> {c.user_domain}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-500">{c.user_domain || '—'} (no match)</span>
                        )}
                      </td>
                      <td className="px-5 py-3"><StatusPill status={c.status} /></td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-2">
                          {c.status === 'pending' ? (
                            <>
                              <button onClick={() => act(c, 'approve')} disabled={busyId === c.id} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-white disabled:opacity-60" style={{ backgroundColor: BRAND_BLUE }}>
                                {busyId === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Approve
                              </button>
                              <button onClick={() => act(c, 'reject')} disabled={busyId === c.id} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-red-600 border border-red-200 hover:bg-red-50 disabled:opacity-60">
                                <X className="w-3 h-3" /> Reject
                              </button>
                            </>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50/80 border-b border-gray-100">
                  <tr>
                    <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase">Company</th>
                    <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase">Comment</th>
                    <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase">By</th>
                    <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="text-right px-5 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {loading ? (
                    <tr><td colSpan={5} className="px-5 py-10 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
                  ) : comments.length === 0 ? (
                    <tr><td colSpan={5} className="px-5 py-10 text-center text-gray-400">No comments</td></tr>
                  ) : comments.map((c) => (
                    <tr key={c.id} className="hover:bg-gray-50/50 transition-colors align-top">
                      <td className="px-5 py-3">
                        <Link to={`/directory/${c.slug}`} target="_blank" className="font-medium text-gray-800 hover:underline flex items-center gap-1">
                          {c.company_name} <ExternalLink className="w-3 h-3 text-gray-400" />
                        </Link>
                        {c.created_at && <div className="text-xs text-gray-400 mt-0.5">{new Date(c.created_at).toLocaleString()}</div>}
                      </td>
                      <td className="px-5 py-3 text-sm text-gray-700 max-w-md"><p className="whitespace-pre-wrap break-words">{c.text}</p></td>
                      <td className="px-5 py-3 text-sm text-gray-600">
                        <div className="flex items-center gap-1.5">
                          {c.user_name}
                          {c.anonymous && (
                            <span
                              title="Submitted from the public site by someone without a Trados account"
                              className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200"
                            >
                              GUEST
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-400">{c.user_email}</div>
                      </td>
                      <td className="px-5 py-3"><StatusPill status={c.status} /></td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-2">
                          {c.status === 'pending' ? (
                            <>
                              <button onClick={() => actComment(c, 'approve')} disabled={busyId === c.id} data-testid={`comment-approve-${c.id}`} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-white disabled:opacity-60" style={{ backgroundColor: BRAND_BLUE }}>
                                {busyId === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Approve
                              </button>
                              <button onClick={() => actComment(c, 'reject')} disabled={busyId === c.id} data-testid={`comment-reject-${c.id}`} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-red-600 border border-red-200 hover:bg-red-50 disabled:opacity-60">
                                <X className="w-3 h-3" /> Reject
                              </button>
                            </>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </Sidebar>
  );
};

export default AdminDirectoryClaims;
